import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import EmailIcon from '@mui/icons-material/Email';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useForm, Controller } from 'react-hook-form';
import { BUTTON_BORDER_RADIUS, TEXTFIELD_BORDER_RADIUS } from 'constants/borderRadius';

/**
 * Email validation regex pattern.
 * @type {RegExp}
 */
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/**
 * @typedef {object} EmailStepProps
 * @property {Function} onSubmit - Callback when email is submitted
 * @property {boolean} [isLoading=false] - Shows loading state on button
 * @property {string} [defaultEmail=''] - Pre-filled email value
 * @property {string} [errorMessage] - External error to display
 * @property {Function} [onClearError] - Callback to clear external error when user types
 */

/**
 * Email input step for the login flow.
 * Validates email format before allowing submission.
 *
 * @param {EmailStepProps} props - Component props
 * @returns {React.ReactElement}
 */
const EmailStep = ({ onSubmit, isLoading = false, defaultEmail = '', errorMessage, onClearError }) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            email: defaultEmail,
        },
    });

    /**
     * Handles form submission
     * @param {{email: string}} data - Form data
     */
    const onFormSubmit = (data) => {
        onSubmit(data.email.trim().toLowerCase());
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            sx={{ mt: 2 }}
        >
            <Controller
                name="email"
                control={control}
                rules={{
                    required: 'Email is required',
                    pattern: {
                        value: EMAIL_PATTERN,
                        message: 'Please enter a valid email address',
                    },
                }}
                render={({ field }) => (
                    <TextField
                        {...field}
                        onChange={(e) => {
                            field.onChange(e);
                            // Clear external error when user starts typing
                            if (errorMessage && onClearError) {
                                onClearError();
                            }
                        }}
                        id="email-input"
                        variant="outlined"
                        placeholder="Enter your email"
                        type="email"
                        fullWidth
                        autoComplete="email"
                        autoFocus
                        disabled={isLoading}
                        error={!!errors.email || !!errorMessage}
                        helperText={errors.email?.message || errorMessage || ' '}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon fontSize='small' color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: TEXTFIELD_BORDER_RADIUS,
                            },
                        }}
                    />
                )}
            />

            <Button
                id="email-continue-btn"
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={isLoading}
                loading={isLoading}
                endIcon={<ArrowForwardIcon />}
                sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
            >
                Continue
            </Button>
        </Box>
    );
};

EmailStep.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    defaultEmail: PropTypes.string,
    errorMessage: PropTypes.string,
    onClearError: PropTypes.func,
};

export default EmailStep;
